import {
	calculateTagDist,
	collectAncestorIds,
	collectDescendantIds,
	findTagById,
	findTopLevelAncestorType,
	getAllItemsFromTags,
	subtreeContainsId,
} from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import type { SessionConfigFiltersType } from "@memorang/constants/config/schema";
import type {
	Distribution,
	Item,
	MixedItemTag,
	SkillTypes,
} from "@memorang/types";
import type { SectionTag } from "@memorang/ui/components/FilterSections/types";
import uniqBy from "lodash/uniqBy";
import type { CheckboxState } from "../types/filter";
import { itemCountForDuration } from "./duration";
import { TAG_TYPES, sortByMasteryPriority } from "./session-filters";

type DistributionKeys = keyof Distribution;

const DEFAULT_MIN_ITEMS = 10;

const SKILL_TYPE_VALUES: SkillTypes[] = [
	"READING",
	"LISTENING",
	"WRITING",
	"SPEAKING",
];

export const getTagSkill = (tag: MixedItemTag): SkillTypes | undefined => {
	if (tag.type !== TAG_TYPES.skill) {
		return undefined;
	}
	const upperLabel = (tag.label ?? tag.title).toUpperCase();
	return SKILL_TYPE_VALUES.find((skill) => skill === upperLabel);
};

export const getSelectedSkills = (
	topLevelTags: MixedItemTag[],
	selectedTagIds: number[],
): SkillTypes[] => {
	const selected = new Set(selectedTagIds);
	const skills = new Set<SkillTypes>();
	for (const tag of topLevelTags) {
		if (selected.has(tag.id)) {
			const skill = getTagSkill(tag);
			if (skill) {
				skills.add(skill);
			}
		}
	}
	return [...skills];
};

const getOptimalValue = (selected: number, current: number) => {
	const selectedOptimal =
		selected > current && current > 0
			? current
			: Math.max(selected, DEFAULT_MIN_ITEMS);
	return selected <= DEFAULT_MIN_ITEMS * 1.5 ? selected : selectedOptimal;
};

const getCheckedDistributionKeys = (checkboxStates: CheckboxState) =>
	checkboxStates
		? (Object.keys(checkboxStates).filter(
				(k) => checkboxStates[k as DistributionKeys],
			) as DistributionKeys[])
		: [];

const calculateNewDistribution = (
	available: Distribution,
	checkedDistKeys: DistributionKeys[],
	numItemsSelected: number,
	distributionFilterItems: SessionConfigFiltersType,
	studyRecommendedTopics?: boolean,
) => {
	const { showItemsSlider, showDurationSelection } =
		useAppStore.getState().app.sessionConfig ?? {};
	const capItemCount = Boolean(showItemsSlider || showDurationSelection);

	const newDist = Object.assign(
		{},
		distributionFilterItems.reduce((acc, item) => {
			acc[item.distKey] = 0;
			return acc;
		}, {} as Distribution),
		{ none: 0 },
	);

	const distributionKeys = Object.keys(newDist) as DistributionKeys[];

	let remaining = numItemsSelected;
	const fillBucket = (key: DistributionKeys) => {
		if (remaining > 0) {
			const numCurrentTotalRequiredItems = Object.keys(newDist).reduce<number>(
				(acc, k) => acc + newDist[k],
				0,
			);
			const avail =
				studyRecommendedTopics && key === "none" ? 0 : available[key];
			if (numCurrentTotalRequiredItems < 50 || !capItemCount) {
				newDist[key] += Math.min(remaining, avail);
				remaining = Math.max(0, remaining - avail);
			}
		}
	};
	if (checkedDistKeys.length) {
		sortByMasteryPriority(checkedDistKeys).forEach(fillBucket);
	} else {
		sortByMasteryPriority(distributionKeys).forEach(fillBucket);
	}

	return newDist;
};

const calculateQuestionCount = (
	checkboxStatus: CheckboxState,
	available: Distribution,
) => {
	const checkedKeys = getCheckedDistributionKeys(checkboxStatus);
	const keys = checkedKeys.length
		? checkedKeys
		: (Object.keys(available) as DistributionKeys[]);
	return keys.reduce((acc, key) => acc + (available[key] ?? 0), 0);
};

const getRequiredDistribution = (
	available: Distribution,
	checkboxStates: CheckboxState,
	numSelectedItems: number,
	distributionFilterItems: SessionConfigFiltersType,
	studyRecommendedTopics?: boolean,
): Distribution =>
	calculateNewDistribution(
		available,
		getCheckedDistributionKeys(checkboxStates),
		numSelectedItems,
		distributionFilterItems,
		studyRecommendedTopics,
	);

const initializeCheckboxStates = (
	availableItems: number,
	availableDist: Distribution,
	capItemCount: boolean,
): CheckboxState => {
	const distributionKeys = Object.keys(availableDist) as DistributionKeys[];
	const masteryLevels = useAppStore.getState().app.masteryLevels!;

	const checkboxStates: CheckboxState = {
		...Object.assign(
			{},
			...masteryLevels.map((item) => ({
				[item.id]: false,
			})),
		),
		none: false,
	};

	let remainingItems = capItemCount
		? Math.min(availableItems, 50)
		: availableItems;

	const orderedKeys = sortByMasteryPriority(distributionKeys);

	for (const bucket of orderedKeys) {
		if (remainingItems > 0 && availableDist[bucket] > 0 && checkboxStates) {
			checkboxStates[bucket] = true;
			remainingItems -= availableDist[bucket];
		}
	}

	return checkboxStates;
};

const sumPositiveDistributionValues = (dist: Distribution) =>
	Object.values(dist).reduce(
		(acc, value) => (value > 0 ? acc + value : acc),
		0,
	);

const deriveDistributionCheckboxStatus = (
	previousStatus: CheckboxState,
	updatedDistribution: Distribution,
): CheckboxState => {
	const next: CheckboxState = {};
	for (const key of Object.keys(updatedDistribution) as DistributionKeys[]) {
		next[key] = !!previousStatus?.[key] && updatedDistribution[key] > 0;
	}
	return next;
};

const calculateCheckboxStatusAndAvailableItems = (
	updatedDistribution: Distribution,
	capItemCount: boolean,
	targetItemCount: number,
	previousCheckboxStatus?: CheckboxState,
) => {
	const availableItems = sumPositiveDistributionValues(updatedDistribution);

	const checkboxStatus = previousCheckboxStatus
		? deriveDistributionCheckboxStatus(
				previousCheckboxStatus,
				updatedDistribution,
			)
		: initializeCheckboxStates(
				targetItemCount,
				updatedDistribution,
				capItemCount,
			);

	return { checkboxStatus, availableItems };
};

type DistributionState = {
	finalAvailableDist: Distribution;
	requiredDistribution: Distribution;
	numRequiredItems: number;
	availableItems: number;
	selectedDistributionCheckboxMap: CheckboxState;
	/** Set only in duration mode; the item count is derived from it on every transition. */
	selectedDurationMinutes?: number;
};

type DistributionAction =
	| { type: "distribution-updated"; distribution: Distribution }
	| { type: "checkbox-selection-changed"; checkboxState: CheckboxState }
	| { type: "required-item-count-changed"; count: number }
	| { type: "duration-changed"; minutes: number };

const durationItemCount = (minutes: number, selectableItems: number) =>
	selectableItems > 0 ? itemCountForDuration(minutes, selectableItems) : 0;

type DistributionReducerConfig = {
	capItemCount: boolean;
	showDistributionFilters?: boolean;
	distributionFilterItems: SessionConfigFiltersType;
	studyRecommendedTopics?: boolean;
	defaultRequiredItems: number;
	getDefaultRequiredItems: (available: number) => number;
	// The item count the default mastery buckets are sized for. Callers whose
	// target can grow after mount (duration presets) seed against their largest
	// target so the selectable pool is never capped below it.
	getCheckboxSeedTarget?: (available: number) => number;
};

const createInitialDistributionState = (
	availableDistribution: Distribution,
	numAvailableItems: number,
	defaultRequiredItems: number,
	selectedDurationMinutes?: number,
): DistributionState => ({
	finalAvailableDist: availableDistribution,
	requiredDistribution: availableDistribution,
	numRequiredItems: defaultRequiredItems,
	availableItems: numAvailableItems,
	selectedDistributionCheckboxMap: undefined,
	selectedDurationMinutes,
});

const createDistributionReducer =
	(config: DistributionReducerConfig) =>
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	(state: DistributionState, action: DistributionAction): DistributionState => {
		if (action.type === "distribution-updated") {
			const availableItemsForDist = sumPositiveDistributionValues(
				action.distribution,
			);
			const isFirstRealDistribution =
				state.selectedDistributionCheckboxMap === undefined;
			const provisionalTarget = isFirstRealDistribution
				? config.getDefaultRequiredItems(availableItemsForDist)
				: state.numRequiredItems;

			const seedTarget = config.getCheckboxSeedTarget
				? config.getCheckboxSeedTarget(availableItemsForDist)
				: provisionalTarget;

			const { availableItems, checkboxStatus } =
				calculateCheckboxStatusAndAvailableItems(
					action.distribution,
					config.capItemCount,
					seedTarget,
					state.selectedDistributionCheckboxMap,
				);

			const selectableItems = calculateQuestionCount(
				checkboxStatus,
				action.distribution,
			);
			const numRequiredItems =
				state.selectedDurationMinutes !== undefined
					? durationItemCount(state.selectedDurationMinutes, selectableItems)
					: config.capItemCount && !isFirstRealDistribution
						? Math.min(state.numRequiredItems, selectableItems)
						: provisionalTarget;

			const requiredDistribution = config.showDistributionFilters
				? getRequiredDistribution(
						action.distribution,
						checkboxStatus,
						numRequiredItems,
						config.distributionFilterItems,
						config.studyRecommendedTopics,
					)
				: action.distribution;

			return {
				...state,
				finalAvailableDist: action.distribution,
				requiredDistribution,
				numRequiredItems,
				availableItems,
				selectedDistributionCheckboxMap: checkboxStatus,
			};
		}

		if (action.type === "checkbox-selection-changed") {
			const questionCount = calculateQuestionCount(
				action.checkboxState,
				state.finalAvailableDist,
			);
			const numRequiredItems =
				state.selectedDurationMinutes !== undefined
					? durationItemCount(state.selectedDurationMinutes, questionCount)
					: config.capItemCount
						? Math.min(
								state.numRequiredItems || config.defaultRequiredItems,
								questionCount,
							)
						: questionCount;

			const requiredDistribution = getRequiredDistribution(
				state.finalAvailableDist,
				action.checkboxState,
				numRequiredItems,
				config.distributionFilterItems,
				config.studyRecommendedTopics,
			);

			return {
				...state,
				availableItems: questionCount,
				requiredDistribution,
				selectedDistributionCheckboxMap: action.checkboxState,
				numRequiredItems,
			};
		}

		if (action.type === "duration-changed") {
			const selectableItems = calculateQuestionCount(
				state.selectedDistributionCheckboxMap,
				state.finalAvailableDist,
			);
			const numRequiredItems = durationItemCount(
				action.minutes,
				selectableItems,
			);
			return {
				...state,
				selectedDurationMinutes: action.minutes,
				numRequiredItems,
				requiredDistribution: getRequiredDistribution(
					state.finalAvailableDist,
					state.selectedDistributionCheckboxMap,
					numRequiredItems,
					config.distributionFilterItems,
					config.studyRecommendedTopics,
				),
			};
		}

		const requiredDistribution = getRequiredDistribution(
			state.finalAvailableDist,
			state.selectedDistributionCheckboxMap,
			action.count,
			config.distributionFilterItems,
			config.studyRecommendedTopics,
		);

		return {
			...state,
			numRequiredItems: action.count,
			requiredDistribution,
		};
	};

const getTagIds = (
	status: CheckboxState,
	freeTagIds?: number[],
	hasAccess?: boolean,
) => {
	const checkedTagIds = status
		? Object.keys(status)
				.filter((key) => status[key])
				.map(Number)
		: [];
	const uncheckedTagIds =
		hasAccess && status
			? Object.keys(status)
					.filter((key) => !status[key])
					.map(Number)
			: freeTagIds;
	return { checkedTagIds, uncheckedTagIds };
};

const getFinalTagIds = (checkedTagIds: number[], uncheckedTagIds: number[]) => {
	return checkedTagIds.length > 0 ? checkedTagIds : uncheckedTagIds;
};

const filterTagsByTagIds = (tags: MixedItemTag[], ids: number[]) => {
	return tags.filter(({ id }) => ids?.includes(id));
};

const dedupeNonOverlappingTags = (
	checkedTags: MixedItemTag[],
	topLevelTags: MixedItemTag[],
): MixedItemTag[] => {
	if (checkedTags.length < 2) {
		return checkedTags;
	}
	const idSet = new Set(checkedTags.map((tag) => tag.id));
	return checkedTags.filter((tag) => {
		const ancestors = collectAncestorIds(topLevelTags, tag.id);
		return !ancestors.some((id) => idSet.has(id));
	});
};

const getFinalTagsDistForMode = (
	tags: MixedItemTag[],
	mode: string,
	showLearningMode?: boolean,
) => {
	if (showLearningMode) {
		const allItems = getAllItemsFromTags(tags);
		return allItems.filter((item) => item?.type === mode);
	}
	return tags;
};

const getIntersectedTagsDistForMode = (
	tags: MixedItemTag[],
	mode: string,
	showLearningMode?: boolean,
	topLevelTags?: MixedItemTag[],
): Item[] | MixedItemTag[] => {
	if (!showLearningMode) {
		return uniqBy(tags, "id");
	}

	const resolveGroupKey = (tag: MixedItemTag) =>
		(topLevelTags && findTopLevelAncestorType(topLevelTags, tag.id)) ||
		tag.type;

	const itemsByType = Object.values(
		tags.reduce<Record<string, MixedItemTag[]>>((acc, tag) => {
			const key = resolveGroupKey(tag);
			if (!acc[key]) {
				acc[key] = [];
			}
			acc[key].push(tag);
			return acc;
		}, {}),
	).map((typeTags) =>
		uniqBy(
			getAllItemsFromTags(typeTags).filter((item) => item?.type === mode),
			"id",
		),
	);

	if (!itemsByType.length) {
		return [];
	}

	const [firstTypeItems, ...remainingTypeItems] = itemsByType;
	const commonIds = remainingTypeItems.reduce<Set<Item["id"]>>(
		(acc, items) => {
			const itemIds = new Set(items.map((item) => item.id));
			const nextCommonIds = new Set<Item["id"]>();
			for (const id of acc) {
				if (itemIds.has(id)) {
					nextCommonIds.add(id);
				}
			}
			return nextCommonIds;
		},
		new Set(firstTypeItems.map((item) => item.id)),
	);

	return firstTypeItems.filter((item) => commonIds.has(item.id));
};

const computeCheckedTagsDist = ({
	status,
	allTags,
	topLevelTags,
	freeTagIds,
	hasAccess,
	crossTagSelection,
	learningMode,
	showLearningMode,
	distributionFilterItems,
	showPointsBasedScore,
}: {
	status: CheckboxState;
	allTags: MixedItemTag[];
	topLevelTags: MixedItemTag[];
	freeTagIds?: number[];
	hasAccess?: boolean;
	crossTagSelection?: boolean;
	learningMode: string;
	showLearningMode?: boolean;
	distributionFilterItems: SessionConfigFiltersType;
	showPointsBasedScore?: boolean;
}): Distribution => {
	const { checkedTagIds, uncheckedTagIds = [] } = getTagIds(
		status,
		freeTagIds,
		hasAccess,
	);
	const finalTagIds = getFinalTagIds(checkedTagIds, uncheckedTagIds);
	const checkedTags = dedupeNonOverlappingTags(
		filterTagsByTagIds(allTags, finalTagIds),
		topLevelTags,
	);

	const types = checkedTags
		.map((tag) => findTopLevelAncestorType(topLevelTags, tag.id))
		.filter((type): type is string => Boolean(type));
	const isMixedType = new Set(types).size > 1 && crossTagSelection;

	const tagsDistForMode = getFinalTagsDistForMode(
		checkedTags,
		learningMode,
		showLearningMode,
	);

	const seenIds = new Set();
	const uniqueTagsDist = tagsDistForMode.filter((entry) => {
		if (seenIds.has(entry.id)) {
			return false;
		}
		seenIds.add(entry.id);
		return true;
	});

	const finalTagsDist = isMixedType
		? getIntersectedTagsDistForMode(
				checkedTags,
				learningMode,
				showLearningMode,
				topLevelTags,
			)
		: uniqueTagsDist;

	return calculateTagDist(
		finalTagsDist,
		distributionFilterItems,
		showPointsBasedScore,
	);
};

const calculateProficiencyBasedDistribution = (
	tags: MixedItemTag[],
	distributionFilterItems: SessionConfigFiltersType,
) => {
	const result = tags.map((tag) => {
		const subtreeItems = uniqBy(getAllItemsFromTags([tag]), "id");
		return {
			...tag,
			distribution: subtreeItems.reduce(
				(acc, item) => {
					const proficiencyLevel = getProficiencyLevel(
						tag.label!,
						item.stats?.score,
					);
					acc[proficiencyLevel] = (acc[proficiencyLevel] || 0) + 1;
					return acc;
				},
				{
					...distributionFilterItems.reduce(
						(acc, item) => {
							acc[item.distKey] = 0;
							return acc;
						},
						{} as Record<string, number>,
					),
				} as Record<string, number>,
			),
		};
	});

	const accumulatedDistribution = result.reduce(
		(acc, item) => {
			for (const key of Object.keys(item.distribution)) {
				acc[key] = (acc[key] || 0) + item.distribution[key];
			}
			return acc;
		},
		{} as Record<string, number>,
	);

	return {
		mappedTags: result,
		accumulatedDistribution,
	};
};

const getProficiencyLevel = (
	skill: string,
	score?: number,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
): "advanced" | "intermediate" | "basic" | "none" => {
	if (score == null) {
		return "none";
	}

	switch (skill) {
		case "Reading": {
			if (score >= 80) {
				return "advanced";
			}
			if (score >= 13) {
				return "intermediate";
			}
			return "basic";
		}

		case "Listening": {
			if (score >= 73) {
				return "advanced";
			}
			if (score >= 30) {
				return "intermediate";
			}
			return "basic";
		}

		case "Speaking": {
			if (score >= 83) {
				return "advanced";
			}
			if (score >= 33) {
				return "intermediate";
			}
			return "basic";
		}

		case "Writing": {
			if (score >= 80) {
				return "advanced";
			}
			if (score >= 43) {
				return "intermediate";
			}
			return "basic";
		}

		default:
			throw new Error(`Unknown skill: ${skill}`);
	}
};

const mapTagItemsToProficiencyDistribution = (
	tags: MixedItemTag[],
): MixedItemTag[] =>
	tags.map((tag) => ({
		...tag,
		items: tag.items?.map((item) => ({
			...item,
			distribution: getProficiencyLevel(tag.label!, item.stats?.score),
		})),
		children: tag.children
			? mapTagItemsToProficiencyDistribution(tag.children)
			: tag.children,
	}));

const applySubtreeExclusiveToggle = (
	map: CheckboxState | undefined,
	tagId: number,
	topLevelTags: MixedItemTag[],
): CheckboxState => {
	const next: CheckboxState = { ...(map || {}) };
	const wasChecked = !!next[tagId];

	if (wasChecked) {
		next[tagId] = false;
		return next;
	}

	const ancestorIds = collectAncestorIds(topLevelTags, tagId);
	for (const id of ancestorIds) {
		next[id] = false;
	}

	const node = findTagById(topLevelTags, tagId);
	if (node) {
		for (const id of collectDescendantIds(node)) {
			next[id] = false;
		}
	}

	next[tagId] = true;
	return next;
};

const collectLockedTagIds = (tags: MixedItemTag[]): Set<number> => {
	const lockedIds = new Set<number>();
	const visit = (tag: MixedItemTag) => {
		if (tag.locked) {
			lockedIds.add(tag.id);
		}
		for (const child of tag.children ?? []) {
			visit(child);
		}
	};
	for (const tag of tags) {
		visit(tag);
	}
	return lockedIds;
};

const resolveSessionStartTagIds = (
	selectedTagCheckboxMap: CheckboxState,
	sectionTags: SectionTag[] | undefined,
	hasAccess?: boolean,
	freeTagIds?: number[],
): number[] => {
	const allSectionTags = sectionTags?.flatMap((section) => section.tags) ?? [];
	const lockedTagIds = collectLockedTagIds(allSectionTags);
	const emptyTagIds = new Set(
		allSectionTags
			.filter((tag) => (tag.numItems ?? 0) === 0)
			.map((tag) => tag.id),
	);

	const selectedTagIds = selectedTagCheckboxMap
		? Object.keys(selectedTagCheckboxMap)
				.filter(
					(tagId) =>
						selectedTagCheckboxMap[tagId] &&
						!lockedTagIds.has(Number(tagId)) &&
						!emptyTagIds.has(Number(tagId)),
				)
				.map(Number)
		: [];

	if (selectedTagIds.length > 0) {
		return selectedTagIds;
	}

	if (hasAccess) {
		return allSectionTags.map((tag) => tag.id);
	}
	return (freeTagIds ?? []).filter((id) =>
		allSectionTags.some((tag) => subtreeContainsId(tag, id)),
	);
};

const resolveRecommendedSessionTagIds = (
	tagIds: number[],
	recommendationTags: MixedItemTag[],
	freeTagIds?: number[],
): number[] => {
	const lockedIds = new Set(
		recommendationTags.filter((tag) => tag.locked).map((tag) => tag.id),
	);
	const unlockedTagIds = tagIds.filter((id) => !lockedIds.has(id));
	if (unlockedTagIds.length > 0) {
		return unlockedTagIds;
	}
	return (freeTagIds ?? []).filter((id) =>
		recommendationTags.some((tag) => subtreeContainsId(tag, id)),
	);
};

const groupSelectedTagsByTopLevelType = (
	selectedTagIds: number[],
	topLevelTags: MixedItemTag[],
): number[][] => {
	const groupedByType: Record<string, number[]> = {};

	for (const tagId of selectedTagIds) {
		const ancestor = topLevelTags.find((top) => subtreeContainsId(top, tagId));
		if (!ancestor) {
			continue;
		}
		const type = ancestor.type;
		if (!groupedByType[type]) {
			groupedByType[type] = [];
		}
		groupedByType[type].push(tagId);
	}

	return Object.values(groupedByType);
};

export {
	getOptimalValue,
	calculateQuestionCount,
	getRequiredDistribution,
	initializeCheckboxStates,
	calculateCheckboxStatusAndAvailableItems,
	createDistributionReducer,
	createInitialDistributionState,
	filterTagsByTagIds,
	getIntersectedTagsDistForMode,
	computeCheckedTagsDist,
	calculateProficiencyBasedDistribution,
	applySubtreeExclusiveToggle,
	resolveSessionStartTagIds,
	resolveRecommendedSessionTagIds,
	mapTagItemsToProficiencyDistribution,
	groupSelectedTagsByTopLevelType,
};
